import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

const HorizontalNewsCard = ({
  image,
  category,
  state,
  title,
  author,
  date,
  readTime,
  fullNews,
  videoUrl,
  link,
  id,
  compact = false,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const synthRef = useRef(window.speechSynthesis);

  useEffect(() => {
    const synth = synthRef.current;
    const loadVoices = () => synth.getVoices();
    loadVoices();
    if (synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = loadVoices;
    }
    return () => synth.cancel();
  }, []);

  useEffect(() => {
    return () => window.speechSynthesis.cancel();
  }, []);

  const cleanText = (text) => {
    if (!text) return "";
    return text.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    try {
      const shareUrl = new URL(link || `/news/${id || 1}`, window.location.origin).href;
      if (navigator.share) {
        await navigator.share({ title: title || "News", text: title || "Check this news", url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link copied successfully!");
      }
    } catch (error) {
      console.log("Share failed:", error);
    }
  };

  const handleListen = (e) => {
    e.stopPropagation();
    const synth = synthRef.current;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    synth.cancel();
    const cleanedText = cleanText(fullNews);
    
    // fallback if no fullNews
    const textToSpeak = cleanedText || cleanText(title);
    if(!textToSpeak) return;

    const voices = synth.getVoices();
    const hindiVoice = voices.find((v) => v.lang === "hi-IN") || voices.find((v) => v.lang.includes("hi"));
    const chunks = textToSpeak.match(/.{1,180}/g);
    let index = 0;

    const speakChunk = () => {
      if (!chunks || index >= chunks.length) {
        setSpeaking(false);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(chunks[index]);
      if (hindiVoice) utterance.voice = hindiVoice;
      else utterance.lang = "hi-IN";
      utterance.rate = 0.85;
      utterance.onend = () => { index++; speakChunk(); };
      utterance.onerror = () => setSpeaking(false);
      synth.speak(utterance);
    };
    speakChunk();
    setSpeaking(true);
  };

  const targetLink = link || (id ? `/news/${id}` : "/news/1");
  const displayCategory = state ? state : (category && category.trim() ? category : "सामान्य");

  return (
    <div
      onClick={() => navigate(targetLink)}
      className={`group flex ${compact ? 'flex-row items-center' : 'flex-col md:flex-row'} ${compact ? 'gap-4 p-3 md:p-4' : 'gap-3 p-3 md:p-3.5'} border border-gray-200/70 rounded-xl cursor-pointer bg-white relative overflow-hidden hover:border-gray-300 hover:shadow-sm transition-all duration-300 w-full`}
    >
      {/* THUMBNAIL (LEFT) */}
      <div className={`${compact ? 'w-[140px] md:w-[160px] shrink-0' : 'w-full md:w-[180px] lg:w-[190px] shrink-0'} aspect-video relative rounded-lg overflow-hidden bg-gray-100`}>
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {!compact && (
          <div className="absolute top-2 right-2 bg-brand-red/90 backdrop-blur-sm text-white text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded shadow-sm">
            {displayCategory}
          </div>
        )}
        {videoUrl && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <div className={`rounded-full bg-brand-red/90 text-white flex items-center justify-center shadow-lg ${compact ? 'w-8 h-8' : 'w-8 h-8 md:w-10 md:h-10'}`}>
              <i className={`fa-solid fa-play ml-0.5 ${compact ? 'text-[10px]' : 'text-xs md:text-sm'}`}></i>
            </div>
          </div>
        )}
      </div>

      {/* CONTENT (RIGHT) */}
      <div className="flex-1 flex flex-col justify-center py-0.5">
        <h3 className={`font-normal ${compact ? 'text-[14px] md:text-[16px]' : 'text-[15px] md:text-[16px]'} leading-snug text-gray-900 group-hover:text-brand-red transition-colors mb-1.5`}>
          {title}
        </h3>
        
        {/* SNIPPET / FULL NEWS CLAMP */}
        <p className={`text-justify text-gray-600 w-full ${compact ? 'text-[12px] line-clamp-2 mt-1' : 'text-[13px] line-clamp-2'} leading-relaxed mb-3`}>
          {cleanText(fullNews)}
        </p>

        {/* META & ACTIONS */}
        <div className={`mt-auto pt-3 pb-1 border-t border-gray-100/60 flex justify-between items-center opacity-90 group-hover:opacity-100 transition-opacity w-full`}>
          <div className="flex gap-3.5">
            <button
              onClick={(e) => {
                  if (videoUrl) {
                      e.stopPropagation();
                      window.open(videoUrl, "_blank");
                  } else {
                      e.stopPropagation();
                      navigate(targetLink);
                  }
              }}
              className="text-red-500 hover:text-red-600 transition transform hover:scale-110" title="Play Video"
            >
              <i className="fa-solid fa-circle-play text-[15px] md:text-[17px]"></i>
            </button>
            <button
              onClick={handleListen}
              className={`transition transform hover:scale-110 ${speaking ? 'text-brand-red animate-pulse' : 'text-blue-500 hover:text-blue-600'}`}
              title="Sound"
            >
              <i className={`fa-solid ${speaking ? 'fa-volume-xmark' : 'fa-volume-high'} text-[14px] md:text-[16px]`}></i>
            </button>
          </div>
          <div className="flex gap-3.5 items-center">
            <button 
              onClick={handleShare}
              className="text-green-500 hover:text-green-600 transition transform hover:scale-110" title="Share"
            >
              <i className="fa-solid fa-share-nodes text-[14px] md:text-[16px]"></i>
            </button>
            <button 
              onClick={(e) => {
                  e.stopPropagation();
                  navigate(targetLink);
              }}
              className="text-gray-400 hover:text-gray-600 transition transform hover:scale-110" title="More"
            >
              <i className="fa-solid fa-ellipsis-vertical text-[14px] md:text-[16px]"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorizontalNewsCard;
